export const COUNTRY_SLUG_MAP: Record<string, string> = {
  'uk': 'UK',
  'germany': 'Germany',
  'france': 'France',
  'spain': 'Spain',
  'sweden': 'Sweden',
  'netherlands': 'Netherlands',
  'switzerland': 'Switzerland',
  'italy': 'Italy',
  'finland': 'Finland',
  'denmark': 'Denmark',
  'belgium': 'Belgium',
  'turkey': 'Türkiye',
  'ireland': 'Ireland',
  'poland': 'Poland',
  'austria': 'Austria',
  'norway': 'Norway',
  'estonia': 'Estonia',
  'portugal': 'Portugal',
  'lithuania': 'Lithuania',
  'ukraine': 'Ukraine',
  'latvia': 'Latvia',
  'czech-republic': 'Czech Republic',
  'iceland': 'Iceland',
  'romania': 'Romania',
  'greece': 'Greece',
  'luxembourg': 'Luxembourg',
  'bulgaria': 'Bulgaria',
  'hungary': 'Hungary',
  'croatia': 'Croatia',
  'cyprus': 'Cyprus',
};

export const COUNTRY_ROUND_COUNTS: Record<string, number> = {
  'UK': 11821,
  'Germany': 7160,
  'France': 5635,
  'Spain': 3532,
  'Sweden': 3223,
  'Netherlands': 2660,
  'Switzerland': 2381,
  'Italy': 1361,
  'Finland': 1209,
  'Denmark': 1093,
  'Belgium': 998,
  'Türkiye': 978,
  'Ireland': 876,
  'Poland': 663,
  'Austria': 648,
  'Norway': 636,
  'Estonia': 623,
  'Portugal': 337,
  'Lithuania': 279,
  'Ukraine': 255,
  'Latvia': 237,
  'Czech Republic': 217,
  'Iceland': 184,
  'Romania': 164,
  'Greece': 157,
  'Luxembourg': 129,
  'Bulgaria': 115,
  'Hungary': 110,
  'Croatia': 57,
  'Cyprus': 53,
};

export const REGION_MAP: Record<string, { label: string; countries: string[] }> = {
  'europe': {
    label: 'Europe',
    countries: Object.values(COUNTRY_SLUG_MAP),
  },
  'western-europe': {
    label: 'Western Europe',
    countries: ['UK', 'Germany', 'France', 'Netherlands', 'Switzerland', 'Belgium', 'Luxembourg', 'Austria'],
  },
  'northern-europe': {
    label: 'Northern Europe',
    countries: ['Sweden', 'Finland', 'Denmark', 'Norway', 'Iceland'],
  },
  'southern-europe': {
    label: 'Southern Europe',
    countries: ['Spain', 'Italy', 'Portugal', 'Greece', 'Cyprus'],
  },
  'eastern-europe': {
    label: 'Eastern Europe',
    countries: ['Poland', 'Czech Republic', 'Hungary', 'Romania', 'Ukraine', 'Bulgaria', 'Croatia', 'Estonia', 'Lithuania', 'Latvia'],
  },
  'dach': {
    label: 'DACH',
    countries: ['Germany', 'Austria', 'Switzerland'],
  },
  'benelux': {
    label: 'Benelux',
    countries: ['Netherlands', 'Belgium', 'Luxembourg'],
  },
  'nordics': {
    label: 'Nordics',
    countries: ['Sweden', 'Finland', 'Denmark', 'Norway', 'Iceland'],
  },
  'baltics': {
    label: 'Baltics',
    countries: ['Estonia', 'Lithuania', 'Latvia'],
  },
  'turkey': {
    label: 'Turkey',
    countries: ['Türkiye'],
  },
};

export const STAGE_FILTER_OPTIONS: { value: string | null; label: string }[] = [
  { value: null, label: 'All Stages' },
  { value: 'Pre-Seed', label: 'Pre-Seed' },
  { value: 'Seed', label: 'Seed' },
  { value: 'Early VC', label: 'Early VC' },
  { value: 'Series A', label: 'Series A' },
  { value: 'Series B', label: 'Series B' },
  { value: 'Series C', label: 'Series C' },
  { value: 'Series D+', label: 'Series D+' },
  { value: 'Growth/Late', label: 'Growth/Late' },
];

export const YEAR_FILTER_OPTIONS: { value: number | null; label: string }[] = [
  { value: null, label: 'All Years' },
  { value: 2026, label: '2026 (YTD)' },
  { value: 2025, label: '2025' },
  { value: 2024, label: '2024' },
  { value: 2023, label: '2023' },
  { value: 2022, label: '2022' },
  { value: 2021, label: '2021' },
  { value: 2020, label: '2020' },
];

export const TOP_SECTORS = [
  'Software', 'Fintech', 'Healthtech', 'AI', 'Cleantech',
  'Cybersecurity', 'E-commerce', 'Media', 'Deeptech', 'Hardware',
  'Transportation', 'Edtech', 'Proptech', 'Gaming', 'HRtech',
];

export const EXCLUDED_COUNTRIES = [
  'Russia', 'Israel', 'United States', 'Canada', 'Australia',
  'New Zealand', 'Uzbekistan', 'Palestine', 'Ethiopia', 'Georgia', 'Armenia',
];

/** Given a slug, returns the DB country name(s) to filter on */
export function resolveSlugToCountries(slug: string): string[] | null {
  if (REGION_MAP[slug]) return REGION_MAP[slug].countries;
  if (COUNTRY_SLUG_MAP[slug]) return [COUNTRY_SLUG_MAP[slug]];
  return null;
}

/** Given a slug, returns the display label */
export function resolveSlugToLabel(slug: string): string {
  if (REGION_MAP[slug]) return REGION_MAP[slug].label;
  if (COUNTRY_SLUG_MAP[slug]) {
    const db = COUNTRY_SLUG_MAP[slug];
    return db === 'UK' ? 'the UK' : db;
  }
  return slug;
}

export const STAGE_SLUG_MAP: Record<string, { dbValue: string; label: string; description: string }> = {
  'seed': {
    dbValue: 'Seed',
    label: 'Seed',
    description: 'Seed-stage venture capital investors funding early startups',
  },
  'series-a': {
    dbValue: 'Series A',
    label: 'Series A',
    description: 'Series A investors backing startups scaling their first product',
  },
  'series-b': {
    dbValue: 'Series B',
    label: 'Series B',
    description: 'Series B investors funding proven startups accelerating growth',
  },
  'early-vc': {
    dbValue: 'Early VC',
    label: 'Early VC',
    description: 'Early-stage venture capital investors at pre-Series A stages',
  },
  'pre-seed': {
    dbValue: 'Pre-Seed',
    label: 'Pre-Seed',
    description: 'Pre-seed investors backing founders at idea and prototype stage',
  },
  'series-c': {
    dbValue: 'Series C',
    label: 'Series C',
    description: 'Series C investors funding expansion and market leadership rounds',
  },
  'growth-late': {
    dbValue: 'Growth/Late',
    label: 'Growth / Late Stage',
    description: 'Growth and late-stage investors backing mature, scaling companies',
  },
  'series-d-plus': {
    dbValue: 'Series D+',
    label: 'Series D+',
    description: 'Late-stage and pre-IPO investors in Series D and beyond',
  },
};

export const STAGE_QUALIFYING_COUNTRIES: Record<string, string[]> = {
  'seed': ['uk','germany','france','spain','sweden','netherlands','switzerland','italy','finland','denmark','belgium','turkey','ireland','poland','austria','norway','estonia','portugal','lithuania','ukraine','latvia','czech-republic','iceland','romania','greece','luxembourg','bulgaria','hungary','croatia','cyprus'],
  'series-a': ['uk','germany','france','spain','sweden','netherlands','switzerland','italy','finland','denmark','belgium','turkey','ireland','poland','austria','norway','estonia','luxembourg','iceland','greece','hungary','czech-republic','portugal','romania','lithuania','ukraine'],
  'series-b': ['uk','germany','france','spain','sweden','netherlands','switzerland','italy','finland','denmark','belgium','ireland','austria','norway','poland','czech-republic','luxembourg','turkey','estonia','portugal','croatia'],
  'early-vc': ['uk','germany','france','spain','sweden','netherlands','switzerland','italy','finland','denmark','belgium','ireland','norway','czech-republic','austria','latvia','poland'],
  'pre-seed': ['uk','germany','france','spain','sweden','netherlands','switzerland','italy','finland','denmark','estonia','ireland','poland','turkey','lithuania','latvia'],
  'series-c': ['uk','germany','france','netherlands','switzerland','spain','sweden','ireland','finland','italy','belgium'],
  'growth-late': ['uk','germany','france','netherlands','switzerland','sweden','norway','spain','ireland','finland','italy'],
  'series-d-plus': ['uk','germany','france','netherlands','switzerland','sweden','spain','finland','belgium'],
};

export const STAGE_EU_ROUND_COUNTS: Record<string, number> = {
  'seed': 10883,
  'series-a': 5306,
  'series-b': 2371,
  'early-vc': 2174,
  'pre-seed': 1700,
  'series-c': 958,
  'growth-late': 847,
  'series-d-plus': 546,
};

/** Country slug → ISO 3166-1 alpha-2 for react-simple-maps */
export const COUNTRY_ISO_MAP: Record<string, string> = {
  'UK': 'GBR',
  'Germany': 'DEU',
  'France': 'FRA',
  'Spain': 'ESP',
  'Sweden': 'SWE',
  'Netherlands': 'NLD',
  'Switzerland': 'CHE',
  'Italy': 'ITA',
  'Finland': 'FIN',
  'Denmark': 'DNK',
  'Belgium': 'BEL',
  'Türkiye': 'TUR',
  'Ireland': 'IRL',
  'Poland': 'POL',
  'Austria': 'AUT',
  'Norway': 'NOR',
  'Estonia': 'EST',
  'Portugal': 'PRT',
  'Lithuania': 'LTU',
  'Ukraine': 'UKR',
  'Latvia': 'LVA',
  'Czech Republic': 'CZE',
  'Iceland': 'ISL',
  'Romania': 'ROU',
  'Greece': 'GRC',
  'Luxembourg': 'LUX',
  'Bulgaria': 'BGR',
  'Hungary': 'HUN',
  'Croatia': 'HRV',
  'Cyprus': 'CYP',
};

/**
 * Maps country display names (as used in existing /investors/country/* pages)
 * to their most-active slug. Handles edge cases like United Kingdom → uk.
 */
export const COUNTRY_DISPLAY_NAME_TO_SLUG: Record<string, string> = {
  'United Kingdom': 'uk',
  'Germany': 'germany',
  'France': 'france',
  'Spain': 'spain',
  'Sweden': 'sweden',
  'Netherlands': 'netherlands',
  'Switzerland': 'switzerland',
  'Italy': 'italy',
  'Finland': 'finland',
  'Denmark': 'denmark',
  'Belgium': 'belgium',
  'Turkey': 'turkey',
  'Türkiye': 'turkey',
  'Ireland': 'ireland',
  'Poland': 'poland',
  'Austria': 'austria',
  'Norway': 'norway',
  'Estonia': 'estonia',
  'Portugal': 'portugal',
  'Lithuania': 'lithuania',
  'Ukraine': 'ukraine',
  'Latvia': 'latvia',
  'Czech Republic': 'czech-republic',
  'Iceland': 'iceland',
  'Romania': 'romania',
  'Greece': 'greece',
  'Luxembourg': 'luxembourg',
  'Bulgaria': 'bulgaria',
  'Hungary': 'hungary',
  'Croatia': 'croatia',
  'Cyprus': 'cyprus',
};

/** ISO alpha-3 → slug for map click navigation */
export const ISO_TO_SLUG: Record<string, string> = Object.fromEntries(
  Object.entries(COUNTRY_SLUG_MAP).map(([slug, dbName]) => [
    COUNTRY_ISO_MAP[dbName] ?? '',
    slug,
  ]).filter(([iso]) => iso !== '')
);
